/**
 * Dm input number component
 */
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DoCheck,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import {
  get,
  has,
  includes,
  isFinite,
  isNumber,
  isNaN,
  values,
  isInteger,
} from 'lodash';

import {
  Observable,
  observableCombineLatest,
  observableOf,
  rx,
  Subscription,
} from '@shared/rx';
import {
  asObservable,
  ControlDirtyAndTouchChangeNotifier,
  floatNumberRoundingFormat,
} from '@shared/utils';
import { XVIInputComponent } from '../input/input.component';
import { IWidgetData } from '../services';
import { DecimalNumber } from '@app/shared/common/decimal-number';
import { DEFAULT_INPUT_THOUSANDS_DIVIDER } from '@config/global-constants';

interface IKeys {
  [key: string]: string;
}

@Component({
  selector: 'xvi-input-number',
  templateUrl: './input-number.component.html',
  styleUrls: ['./input-number.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class XVIInputNumberComponent
  extends XVIInputComponent
  implements OnInit, DoCheck, OnDestroy
{
  @ViewChild('inputElement', { static: false }) public inputElement: any;
  @Input() public label: string;
  @Input() public name: string = '';
  @Input() public class: string = '';
  @Input() public placeholder: string = '';
  @Input() public control: FormControl = new FormControl();
  @Input() public id: string;
  @Input() public disabled: boolean;
  @Input() public required: boolean;
  @Input() public type: string = 'number';
  @Input() public precision: number;
  @Input() public description: string;
  @Input() public hideArrows: boolean = false;
  @Input() public widgetData: IWidgetData = {};
  @Input() public showTrailingZeros: boolean = false;
  @Input() public set divisor(value: Observable<number> | number) {
    this.divisors$ = asObservable(value);
  }
  public notRoundInitValue: boolean = true;

  public get step(): number {
    return new DecimalNumber(10).pow(-this.precision).valueOf();
  }

  public divisors$: Observable<number> = observableOf(1);
  public userControl: FormControl = new FormControl();
  public validityBadInput: boolean = false;

  public lastEmittedValue: number;
  public currentDivisor: number = 1;
  public onlyInteger: boolean = false;
  @Output() public onChange: EventEmitter<number> = new EventEmitter();
  public readonly forbiddenKeysForInteger: IKeys = {
    E: 'e',
    DOT: '.',
  };

  protected subscriptions: Subscription[] = [];

  /**
   * min precision setting
   */
  protected minDecimalPlacesToCheck?: number;

  private dirtyOrTouchedChangeNotifier = new ControlDirtyAndTouchChangeNotifier(
    this,
  ); // eslint-disable-line

  public constructor(public readonly changeDetectorRef: ChangeDetectorRef) {
    super();
  }

  public ngOnInit(): void {
    this.onlyInteger = get(this, 'widgetData.onlyInteger', this.onlyInteger);

    this.subscriptions.push(
      observableCombineLatest([
        this.control.valueChanges.pipe(rx.startWith(this.control.value)),
        this.divisors$,
      ]).subscribe(([inputValue, divisor]: [number, number]) => {
        // if the divisor changes we should recalculate and set control value
        // at the same time the userControl value stays the same
        const newDivisor: number = divisor || 1;
        if (
          inputValue === this.lastEmittedValue &&
          this.currentDivisor !== newDivisor
        ) {
          this.currentDivisor = newDivisor;
          this.setControlValue(parseFloat(this.userControl.value) * newDivisor);
          this.changeDetectorRef.markForCheck();

          return;
        }
        this.currentDivisor = newDivisor;

        // null or string should clear the user input
        if (!isFinite(inputValue) && !isNaN(inputValue)) {
          this.userControl.setValue('');
          this.changeDetectorRef.markForCheck();

          return;
        }
        // finite numbers that differs from previously set value should be
        // transferred to the userControl
        if (isFinite(inputValue) && inputValue !== this.lastEmittedValue) {
          this.lastEmittedValue = inputValue;
          const newValue = floatNumberRoundingFormat(
            inputValue / this.currentDivisor,
            {
              precision: this.precision,
              thousandsSeparator: DEFAULT_INPUT_THOUSANDS_DIVIDER,
              isAddTrailZeros: this.showTrailingZeros,
              minDecimalPlacesToCheck: this.minDecimalPlacesToCheck,
            },
          );

          const isIntegerValue: boolean = isInteger(Number(inputValue));
          this.userControl.setValue(
            this.notRoundInitValue && !isIntegerValue ? inputValue : newValue,
          );
          this.changeDetectorRef.markForCheck();

          return;
        }
      }),
    );

    if (
      !isNumber(this.control?.value) &&
      has(this.widgetData, 'defaultValue')
    ) {
      this.control.setValue(this.widgetData.defaultValue);
      this.control.parent.updateValueAndValidity();
    }
  }

  public ngDoCheck(): void {
    this.dirtyOrTouchedChangeNotifier.check();
  }

  public ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  public focus(): void {
    this.inputElement.nativeElement.focus();
  }

  public onNumberChanged(event): void {
    this.validityBadInput = get(
      event,
      ['target', 'validity', 'badInput'],
      false,
    );
    const newValue: number = this.getFieldValueAsNumber();
    if (
      newValue !== this.control.value &&
      !(isNaN(newValue) && isNaN(this.control.value))
    ) {
      this.setControlValue(newValue);
    }
  }

  public getFieldValueAsNumber(): number {
    if (this.validityBadInput) {
      return Number.NaN;
    }

    return this.userControl.value === ''
      ? null
      : parseFloat(this.userControl.value) * this.currentDivisor;
  }

  public handleScroll(event): void {
    if (document.activeElement === event.target) {
      event.target.blur();
      setTimeout(() => event.target.focus());
    }
  }

  public setControlValue(value): void {
    this.control.markAsDirty();
    this.control.markAsTouched();
    this.lastEmittedValue = value;
    this.control.setValue(value);
    this.control.parent.updateValueAndValidity();
    this.onChange.emit(value);
  }

  public markControlAsTouched(): void {
    this.control.markAsTouched();
  }

  public onKeyDown(event: KeyboardEvent): void {
    const key = get(event, 'key', null);
    const isForbiddenKeyCode = includes(
      values(this.forbiddenKeysForInteger),
      key,
    );

    if (isForbiddenKeyCode && this.onlyInteger) {
      event.preventDefault();
    }
  }
}
